import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';

export interface PlanBlockedData {
  tier: string;
  limit: number | null;
  used: number | null;
  reason: string | null;
  renewsOn: string | null;
}

/**
 * The hard paywall. Unlike FreeTierDialog - which nudges a free user who still
 * has allowance left - this one appears when the allowance is gone and there
 * is no "maybe later": the only ways out are checkout or sign-out.
 */
@Component({
  selector: 'plan-blocked-dialog',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, CommonModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <svg class="logo" width="56" height="56" viewBox="0 0 80 80" fill="none">
          <rect width="80" height="80" rx="18" fill="#f97316"/>
          <path d="M25 18V62H55" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="25" cy="18" r="5" fill="white"/>
          <circle cx="55" cy="62" r="5" fill="white"/>
          <circle cx="40" cy="62" r="3" fill="white" opacity="0.6"/>
          <circle cx="25" cy="40" r="3" fill="white" opacity="0.6"/>
        </svg>
        <h2 class="dialog-title">{{ title }}</h2>
      </div>

      <div class="dialog-content">
        <p class="dialog-message">{{ message }}</p>

        <div class="usage-details" *ngIf="data?.limit">
          <div class="usage-bar">
            <div class="usage-fill"></div>
          </div>
          <div class="usage-numbers">
            <span><strong>{{ data.used | number }}</strong> of {{ data.limit | number }} memories used</span>
            <span class="usage-pill">0 left</span>
          </div>
          <div class="renew-note" *ngIf="data?.renewsOn">
            Your allowance refills on {{ data.renewsOn }}.
          </div>
        </div>

        <div class="upgrade-banner">
          <div class="upgrade-header">
            <span class="upgrade-badge">Required to continue</span>
            <h3>{{ isFree ? 'Upgrade to Personal Plan' : 'Extend your plan' }}</h3>
          </div>
          <div class="starter-benefits">
            <div class="benefit-item">
              <span class="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2v20M2 12h20"/>
                </svg>
              </span>
              <div>
                <strong>More memories every month</strong>
                <span>Pick up right where you left off</span>
              </div>
            </div>
            <div class="benefit-item">
              <span class="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
              </span>
              <div>
                <strong>No interruptions</strong>
                <span>Chat, capture and recall without limits</span>
              </div>
            </div>
            <div class="benefit-item">
              <span class="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </span>
              <div>
                <strong>Everything stays put</strong>
                <span>Your existing memories are safe</span>
              </div>
            </div>
          </div>
        </div>

        <p class="locked-note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          The rest of the app stays locked until your plan is active again.
        </p>
      </div>

      <div class="dialog-actions">
        <button class="upgrade-button" (click)="onUpgrade()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          {{ isFree ? 'Upgrade Now' : 'View Plans' }}
        </button>
        <button class="signout-button" (click)="onLogout()">Sign out</button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      font-family: 'Plus Jakarta Sans', 'Inter', system-ui, sans-serif;
      padding: 0;
      background: #ffffff;
      border-radius: 20px;
      overflow: hidden;
      max-width: 440px;
      /* The actions are the only way out of this dialog, so the body scrolls
         rather than pushing them off a short screen. */
      display: flex;
      flex-direction: column;
      max-height: 88vh;
    }

    .dialog-header {
      background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
      color: white;
      padding: 32px 24px;
      text-align: center;
      position: relative;
      flex-shrink: 0;
    }

    .logo {
      margin-bottom: 16px;
      filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
    }

    .dialog-title {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
      color: white;
    }

    .dialog-content {
      padding: 24px;
      overflow-y: auto;
      flex: 1 1 auto;
    }

    .dialog-message {
      font-size: 15px;
      color: #555;
      margin: 0 0 20px 0;
      text-align: center;
      line-height: 1.5;
    }

    .usage-details {
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 20px;
    }

    .usage-bar {
      height: 8px;
      background: #e9ecef;
      border-radius: 999px;
      overflow: hidden;
    }

    .usage-fill {
      height: 100%;
      width: 100%;
      background: linear-gradient(90deg, #f97316 0%, #dc2626 100%);
    }

    .usage-numbers {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-top: 12px;
      font-size: 13px;
      color: #666;
    }

    .usage-numbers strong {
      color: #333;
    }

    .usage-pill {
      background: rgba(220, 38, 38, 0.1);
      color: #dc2626;
      font-weight: 600;
      font-size: 12px;
      padding: 3px 10px;
      border-radius: 999px;
      white-space: nowrap;
    }

    .renew-note {
      margin-top: 10px;
      font-size: 12px;
      color: #888;
    }

    .upgrade-banner {
      background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
      border: 2px solid #f97316;
      border-radius: 16px;
      padding: 20px;
    }

    .upgrade-header {
      text-align: center;
      margin-bottom: 16px;
    }

    .upgrade-badge {
      display: inline-block;
      background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
      color: white;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 12px;
      border-radius: 20px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }

    .upgrade-banner h3 {
      margin: 0;
      font-size: 20px;
      color: #c2410c;
      font-weight: 700;
    }

    .benefit-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 10px 0;
      border-bottom: 1px solid rgba(249, 115, 22, 0.15);
    }

    .benefit-item:last-child {
      border-bottom: none;
    }

    .benefit-icon {
      width: 32px;
      height: 32px;
      background: rgba(249, 115, 22, 0.15);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .benefit-icon svg {
      width: 16px;
      height: 16px;
      stroke: #f97316;
    }

    .benefit-item div {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .benefit-item strong {
      font-size: 14px;
      color: #333;
      font-weight: 600;
    }

    .benefit-item span {
      font-size: 13px;
      color: #888;
    }

    .locked-note {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 16px 0 0 0;
      font-size: 12px;
      color: #888;
      line-height: 1.4;
    }

    .locked-note svg {
      width: 16px;
      height: 16px;
      stroke: #aaa;
      flex-shrink: 0;
    }

    .dialog-actions {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 20px 24px;
      background: #f8f9fa;
      border-top: 1px solid #e9ecef;
      flex-shrink: 0;
    }

    .upgrade-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
      border: none;
      color: white;
      font-weight: 600;
      font-size: 15px;
      padding: 14px 24px;
      border-radius: 12px;
      cursor: pointer;
      box-shadow: 0 4px 15px rgba(249, 115, 22, 0.35);
      transition: all 0.2s ease;
    }

    .upgrade-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(249, 115, 22, 0.45);
    }

    .upgrade-button svg {
      width: 18px;
      height: 18px;
    }

    .signout-button {
      width: 100%;
      background: transparent;
      border: none;
      color: #888;
      font-size: 13px;
      padding: 8px;
      cursor: pointer;
    }

    .signout-button:hover {
      color: #555;
      text-decoration: underline;
    }

    @media (max-width: 480px) {
      .dialog-container {
        max-width: 95vw;
        border-radius: 16px;
      }

      .dialog-header {
        padding: 24px 20px;
      }

      .dialog-title {
        font-size: 20px;
      }

      .dialog-content {
        padding: 20px;
      }

      .dialog-actions {
        padding: 16px 20px;
      }
    }
  `]
})
export class PlanBlockedDialog {

  constructor(
    public dialogRef: MatDialogRef<PlanBlockedDialog>,
    @Inject(MAT_DIALOG_DATA) public data: PlanBlockedData
  ) { }

  get isFree(): boolean {
    return (this.data?.tier || 'free').toLowerCase() === 'free';
  }

  get title(): string {
    return this.isFree
      ? "You've run out of your Free plan"
      : "You've reached your monthly limit";
  }

  get message(): string {
    if (this.data?.reason) return this.data.reason;
    return this.isFree
      ? 'Your Free plan allowance is used up. Upgrade to keep capturing and recalling memories.'
      : 'This month\'s allowance is used up. Upgrade your plan to keep going.';
  }

  onUpgrade(): void {
    this.dialogRef.close('upgrade');
  }

  onLogout(): void {
    this.dialogRef.close('logout');
  }
}
